import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Car, 
  Phone, 
  Wrench, 
  Shield, 
  CheckCircle, 
  Clock, 
  Award, 
  MapPin, 
  Users,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Hero slider images
  const heroSlides = [
    {
      image: "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Alle Fahrzeugmarken",
      subtitle: "Von Kleinwagen bis Luxuslimousinen",
      description: "Professionelle Autoglas Reparatur für alle Marken"
    },
    {
      image: "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Windschutzscheibe Reparatur",
      subtitle: "Steinschlag & Riss Reparatur",
      description: "Schnelle und professionelle Reparatur in 30-60 Minuten"
    },
    {
      image: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Seitenscheiben Austausch",
      subtitle: "Alle Fahrzeugtypen",
      description: "Hochwertige Original- und Markenersatzteile"
    },
    {
      image: "https://images.unsplash.com/photo-1580273916550-e323be2ae537?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
      title: "Scheibenversiegelung",
      subtitle: "Professioneller Schutz",
      description: "Hydrophobe Beschichtung für optimale Sicht"
    }
  ];

  // Car brand logos
  const carBrandLogos = [
    { name: "Mercedes-Benz", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/90/Mercedes-Logo.svg/2560px-Mercedes-Logo.svg.png" },
    { name: "BMW", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/BMW.svg/2048px-BMW.svg.png" },
    { name: "Audi", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/92/Audi-Logo_2016.svg/2560px-Audi-Logo_2016.svg.png" },
    { name: "Porsche", logo: "https://upload.wikimedia.org/wikipedia/en/thumb/8/8c/Porsche_logo.svg/1200px-Porsche_logo.svg.png" },
    { name: "Jaguar", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/50/Jaguar_2024.svg/2560px-Jaguar_2024.svg.png" },
    { name: "Land Rover", logo: "https://upload.wikimedia.org/wikipedia/en/thumb/4/4a/LandRover.svg/1280px-LandRover.svg.png" },
    { name: "Volvo", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/54/Volvo_logo.svg/2560px-Volvo_logo.svg.png" }
  ];

  // Auto-advance hero slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  // Auto-advance logo slider
  useEffect(() => {
    const timer = setInterval(() => {
      // setCurrentLogo((prev) => (prev + 1) % luxuryLogos.length); // This line is removed
    }, 2000);
    return () => clearInterval(timer);
  }, []); // Changed dependency array to empty array

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Slider Section */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Background Images */}
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="absolute inset-0 bg-black/50"></div>
            </div>
          </div>
        ))}

        {/* Content */}
        <div className="relative z-10 min-h-screen flex items-center justify-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              {/* Logo/Icon */}
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mb-8"
              >
                <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 rounded-full backdrop-blur-sm">
                  <Car size={48} className="text-orange-400" />
                </div>
              </motion.div>
              
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6">
                {heroSlides[currentSlide].title}
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-orange-200">
                {heroSlides[currentSlide].subtitle}
              </p>
              <p className="text-lg md:text-xl mb-12 text-orange-100 max-w-3xl mx-auto">
                {heroSlides[currentSlide].description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="tel:062171895675"
                  className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Phone size={24} />
                  <span>Jetzt anrufen: 0621 71895675</span>
                </motion.a>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/contact"
                    className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-orange-900 px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-300 shadow-lg hover:shadow-xl"
                  >
                    Kontakt aufnehmen
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Slider Navigation */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-4">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-orange-500 scale-125' : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>

        {/* Arrow Navigation */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full backdrop-blur-sm transition-all duration-300"
        >
          <ChevronLeft size={24} />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full backdrop-blur-sm transition-all duration-300"
        >
          <ChevronRight size={24} />
        </button>
      </section>

      {/* Car Brands Section */}
      <section className="py-16 bg-gradient-to-r from-orange-50 to-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-orange-900 mb-4">
              Wir arbeiten mit allen Marken
            </h2>
            <p className="text-xl text-orange-700 max-w-3xl mx-auto">
              Professionelle Autoglas Reparatur für alle Fahrzeugmarken - von Kleinwagen bis Luxuslimousinen
            </p>
          </motion.div>

          {/* Logo Slider */}
          <div className="relative overflow-hidden">
            <div className="flex space-x-12 animate-scroll">
              {[...carBrandLogos, ...carBrandLogos].map((brand, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex-shrink-0 group"
                >
                  <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105 border border-orange-200">
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="h-20 w-20 object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <p className="text-sm text-orange-700 font-medium mt-2 text-center">
                      {brand.name}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-lg text-orange-800 font-medium">
              ✓ Original-Ersatzteile ✓ Garantie ✓ Mobile Reparatur ✓ 24/7 Service
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section - Ana Slider Altı */}
      <section className="py-16 bg-gradient-to-r from-orange-50 to-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-orange-900 mb-4">
              Warum Auto-Glass Mannheim?
            </h2>
            <p className="text-xl text-orange-700 max-w-3xl mx-auto">
              Ihr vertrauensvoller Partner für professionelle Autoglas Reparatur
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <CheckCircle size={48} className="text-green-600" />,
                title: "In der Regel kostenlos",
                description: "Die meisten Autoglas Reparaturen sind über Ihre Versicherung kostenlos. Wir übernehmen die Abwicklung für Sie.",
                bgColor: "bg-green-50",
                borderColor: "border-green-200"
              },
              {
                icon: <Clock size={48} className="text-blue-600" />,
                title: "Innerhalb eines Tages fertig",
                description: "Schnelle Reparatur und Austausch. Die meisten Arbeiten werden noch am selben Tag abgeschlossen.",
                bgColor: "bg-blue-50",
                borderColor: "border-blue-200"
              },
              {
                icon: <Shield size={48} className="text-orange-600" />,
                title: "Scheiben in Originalqualität",
                description: "Wir verwenden ausschließlich hochwertige Original- und Markenersatzteile für optimale Qualität und Sicherheit.",
                bgColor: "bg-orange-50",
                borderColor: "border-orange-200"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`group relative p-8 rounded-xl hover:shadow-xl transition-all duration-300 border-2 ${feature.borderColor} ${feature.bgColor} hover:scale-105`}
                whileHover={{ y: -5 }}
              >
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold text-lg rounded-full hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Phone size={20} className="mr-2" />
              Termin vereinbaren
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-orange-900 mb-4">
              Unsere Leistungen
            </h2>
            <p className="text-xl text-orange-600 max-w-3xl mx-auto">
              Professionelle Autoglas Reparatur und Austausch für alle Fahrzeugtypen
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Wrench size={48} className="text-orange-600" />,
                title: "Autoglas Reparatur",
                description: "Professionelle Reparatur von Steinschlägen und Rissen in der Windschutzscheibe und anderen Autoglaselementen.",
                features: ["Steinschlag Reparatur", "Riss Reparatur", "Professionelle Ausführung", "Garantie"],
                color: "from-orange-400 to-orange-600"
              },
              {
                icon: <Car size={48} className="text-orange-600" />,
                title: "Autoglas Austausch",
                description: "Kompletter Austausch von beschädigten Scheiben mit hochwertigen Original- oder Markenersatzteilen.",
                features: ["Windschutzscheibe", "Seitenscheiben", "Heckfenster", "Qualitätsgarantie"],
                color: "from-orange-500 to-orange-700"
              },
              {
                icon: <Shield size={48} className="text-orange-600" />,
                title: "Scheibenversiegelung",
                description: "Professionelle Versiegelung Ihrer Autoscheiben für optimalen Schutz und bessere Sicht.",
                features: ["Hydrophobe Beschichtung", "UV-Schutz", "Kratzfestigkeit", "Langlebigkeit"],
                color: "from-orange-300 to-orange-500"
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="group relative bg-white p-8 rounded-xl hover:shadow-xl transition-all duration-300 border border-orange-100 hover:border-orange-200"
                whileHover={{ y: -5 }}
              >
                {/* Background gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-10 rounded-xl group-hover:opacity-20 transition-opacity duration-300`}></div>
                
                <div className="relative z-10">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-50 to-orange-100 rounded-full group-hover:from-orange-100 group-hover:to-orange-200 transition-all duration-300">
                      {service.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-orange-900 mb-4 text-center">
                    {service.title}
                  </h3>
                  <p className="text-orange-700 mb-6 text-center">
                    {service.description}
                  </p>
                  <ul className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-3 text-orange-800">
                        <div className="flex-shrink-0 w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center">
                          <CheckCircle size={12} className="text-orange-600" />
                        </div>
                        <span className="text-sm font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Warum Auto-Glass Mannheim?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wir bieten Ihnen die beste Qualität, den schnellsten Service und die fairen Preise
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Clock size={32} className="text-orange-600" />,
                title: "Schneller Service",
                description: "Reparatur in 30-60 Minuten, Austausch am selben Tag möglich"
              },
              {
                icon: <Award size={32} className="text-orange-600" />,
                title: "Qualitätsgarantie",
                description: "Alle Arbeiten mit Garantie, hochwertige Materialien"
              },
              {
                icon: <MapPin size={32} className="text-orange-600" />,
                title: "Mobile Reparatur",
                description: "Wir kommen zu Ihnen - in Mannheim, Frankfurt und Stuttgart"
              },
              {
                icon: <Users size={32} className="text-orange-600" />,
                title: "Erfahrene Techniker",
                description: "Über 15 Jahre Erfahrung in der Autoglas Reparatur"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-white p-6 rounded-xl shadow-sm mb-4 inline-block">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Local SEO Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Autoglas Reparatur in Ihrer Nähe
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wir sind Ihr lokaler Partner für professionelle Autoglas Reparatur in der Region
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                city: "Mannheim",
                description: "Autoglas Reparatur und Austausch in Mannheim und Umgebung. Schneller Service, faire Preise.",
                features: ["Zentrale Lage", "Schneller Service", "Faire Preise", "Qualitätsgarantie"]
              },
              {
                city: "Frankfurt",
                description: "Professionelle Autoglas Reparatur in Frankfurt am Main. Wir kommen auch zu Ihnen nach Hause.",
                features: ["Mobile Reparatur", "24/7 Notfallservice", "Originalteile", "Erfahrene Techniker"]
              },
              {
                city: "Stuttgart",
                description: "Autoglas Reparatur Stuttgart - Ihr vertrauensvoller Partner für alle Autoglas Arbeiten.",
                features: ["Komplettservice", "Günstige Preise", "Sofortige Verfügbarkeit", "Professionelle Ausführung"]
              }
            ].map((location, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-8 rounded-xl"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  Autoglas {location.city}
                </h3>
                <p className="text-gray-600 mb-6 text-center">
                  {location.description}
                </p>
                <ul className="space-y-2">
                  {location.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-2 text-gray-700">
                      <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Brauchen Sie eine Autoglas Reparatur?
            </h2>
            <p className="text-xl mb-8 text-orange-100 max-w-3xl mx-auto">
              Kontaktieren Sie uns jetzt für ein kostenloses Angebot. Wir sind für Sie da - 
              in Mannheim, Frankfurt und Stuttgart.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:062171895675"
                className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-300 flex items-center justify-center space-x-2"
              >
                <Phone size={24} />
                <span>Jetzt anrufen: 0621 71895675</span>
              </a>
              <Link
                to="/contact"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-orange-600 px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-300"
              >
                Kontaktformular
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home; 